import { notification, TablePaginationConfig, Typography } from 'antd';
import { min } from 'lodash';
import { parse, stringify } from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useLocation, useHistory, useParams } from 'react-router-dom';

import { useSortedClassification } from 'src/hooks/useSortedClassification';
import {
  FinancingNeed,
  getFinancingNeed,
  getFinancingNeeds,
  resetFinancingNeedEvent,
} from 'src/slices/financingNeed';
import {
  DatabaseSearchBar,
  SearchFiltersParameters,
} from '../components/DatabaseSearchBar';
import { DonorCards } from '../components/DonorCards';
import { FullscreenSpin } from '../components/FullscreenSpin';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import {
  Donor,
  getDonorsWithAlgorithm,
  resetDonorEvent,
  resetSuggestions,
  searchDonorsWithAlgorithmAndGetSuggestions,
} from '../slices/donor';
import { getNgo } from '../slices/ngo';
import { isOrganizationPremium } from '../utils/projects';

const MAX_DISPLAYED_PAGES_NUMBER = 10;

const { Text } = Typography;

const useStyles = createUseStyles({
  container: {
    padding: '89px 61px',
  },
  fondations: {
    fontFamily: 'PP Mori',
    fontSize: '36px',
    fontWeight: '600',
    lineHeight: '100%',
    letterSpacing: '0%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
  },
});

export const DatabaseFromProject: React.VFC = () => {
  const DONORS_MAX_RESULTS = 10;
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const query = useLocation().search;
  const { projectId } = useParams<{ projectId: string }>();

  const [searchFilters, setSearchFilters] = useState<SearchFiltersParameters>(
    {},
  );
  useEffect(() => {
    console.log('DatabaseFromProject.tsx: searchFilters:', searchFilters);
  }, [searchFilters]);
  const [queryParamsObject, setQueryParamsObject] = useState<
    Record<string, unknown>
  >(parse(query, { arrayFormat: 'bracket' }));

  const {
    donors,
    financingNeeds,
    loading,
    event,
    // addDonorLoading,
    // removeDonorLoading,
    searchResult,
    user,
    offset,
    suggestionsDonors,
    suggestionsLoading,
    classificationsFilter,
    // callForTendersCounts,
  } = useAppSelector(
    ({
      donor: {
        donors,
        loading,
        event,
        suggestionsLoading,
        searchResult,
        suggestionsDonors,
        offset,
        classificationsFilter,
      },
      financingNeed: { financingNeeds, addDonorLoading, removeDonorLoading },
      auth: { user },
      callForTender: { callForTendersCounts },
    }) => ({
      donors,
      classificationsFilter,
      financingNeeds,
      loading,
      event,
      suggestionsLoading,
      addDonorLoading,
      removeDonorLoading,
      searchResult,
      suggestionsDonors,
      user,
      offset,
      callForTendersCounts,
    }),
  );
  const [dropdownFilterVisible, setDropdownFilterVisible] = useState(false);

  const financingNeedEvent = useAppSelector(({ financingNeed: { event } }) => ({
    event,
  })).event;

  const classification = useSortedClassification();

  const isPremium = useMemo(() => {
    return isOrganizationPremium(Object.values(financingNeeds.byId));
  }, [financingNeeds]);
  const fetchDonors = (
    page = 1,
    algorithm = 'tagsProximity',
    searchFilters: SearchFiltersParameters,
  ) => {
    updateQueryParams({ algorithm, page, ...searchFilters });
    dispatch(
      getDonorsWithAlgorithm({
        projectId,
        algorithm: algorithm,
        searchParameters: {
          ...searchFilters,
          offset: (page - 1) * DONORS_MAX_RESULTS,
          limit: DONORS_MAX_RESULTS,
        },
      }),
    );
  };

  const fetchDonorsWithQueryParams = () => {
    const {
      page,
      algorithm,
      name,
      tags,
      statuses,
      targetPersons,
      activityDomains,
      activityZones,
      sustainableDevelopmentGoals,
      targetPopulations,
      donationTypes,
      department,
      modusOperandi,
    } = queryParamsObject;
    const newSearchFilters = {
      name,
      tags,
      statuses,
      targetPersons,
      activityDomains,
      activityZones,
      sustainableDevelopmentGoals,
      targetPopulations,
      donationTypes,
      department,
      modusOperandi,
    } as SearchFiltersParameters;

    setSearchFilters(newSearchFilters);

    fetchDonors(
      page ? parseInt(page as string) : undefined,
      algorithm as string,
      newSearchFilters,
    );
  };

  const updateQueryParams = (params: Record<string, unknown>) => {
    const updatedQueryParamsObject = { ...queryParamsObject, ...params };
    history.replace({
      search: stringify(updatedQueryParamsObject, { arrayFormat: 'bracket' }),
    });
    setQueryParamsObject(updatedQueryParamsObject);
  };

  useEffect(() => {
    dispatch(getNgo(user?.ngoId));
  }, [user?.ngoId]);

  useEffect(() => {
    fetchDonorsWithQueryParams();
    !financingNeeds.allIds.length && dispatch(getFinancingNeeds(user?.ngoId));
  }, []);

  useEffect(() => {
    if (event) {
      event.error
        ? notification.error({
            message:
              event.action == 'addFavoriteDonor'
                ? "Le mécène n'a pas pu être ajouté à vos favoris"
                : "Le mécène n'a pas pu être retiré de vos favoris",
            duration: 2,
          })
        : notification.success({
            message:
              event.action == 'addFavoriteDonor'
                ? 'Le mécène a été ajouté à vos favoris'
                : 'Le mécène a été retiré de vos favoris',
            duration: 2,
          });
      dispatch(resetDonorEvent());
    }
  }, [event]);

  useEffect(() => {
    if (financingNeedEvent) {
      financingNeedEvent.error
        ? notification.error({
            message:
              financingNeedEvent.action == 'addDonor'
                ? "Le mécène n'a pas pu être ajouté au projet"
                : "Le mécène n'a pas pu être retiré du projet",
            duration: 2,
          })
        : notification.success({
            message:
              financingNeedEvent.action == 'addDonor'
                ? 'Le mécène a été ajouté au projet'
                : 'Le mécène a été retiré du projet',
            duration: 2,
          });
      dispatch(resetFinancingNeedEvent());
    }
  }, [financingNeedEvent]);

  const onTableChange = (newPagination: TablePaginationConfig) => {
    const newSearchPage = newPagination.current ?? 0;

    fetchDonors(
      newSearchPage,
      queryParamsObject.algorithm as string,
      searchFilters,
    );
  };

  const onSelectAlgorithm = (algorithm: string) => {
    const { page } = queryParamsObject;

    fetchDonors(
      page ? parseInt(page as string) : undefined,
      algorithm,
      searchFilters,
    );
  };

  const onChangeName = (value: string) => {
    value.length
      ? dispatch(
          searchDonorsWithAlgorithmAndGetSuggestions({
            projectId: projectId,
            searchParameters: { ...searchFilters, offset: 0, name: value },
            algorithm:
              (queryParamsObject.algorithm as string) || 'tagsProximity',
          }),
        )
      : dispatch(resetSuggestions());
  };

  if (!user || !classification || !donors) {
    return <FullscreenSpin />;
  }

  useEffect(() => {
    fetchDonors(1, queryParamsObject.algorithm as string, searchFilters);
  }, [searchFilters]);

  const displayedFinancingNeed = useMemo<FinancingNeed>(
    () => financingNeeds.byId[projectId],
    [financingNeeds.byId[projectId]],
  );

  useEffect(() => {
    dispatch(getFinancingNeed(projectId));
  }, []);

  if (displayedFinancingNeed && isPremium) {
    return (
      <div
        className={classes.container}
        onClick={() => setDropdownFilterVisible(false)}
      >
        <div className={classes.header}>
          <Text className={classes.fondations}>Mécènes</Text>
          <DatabaseSearchBar
            classification={classification}
            onChangeName={onChangeName}
            suggestions={suggestionsDonors}
            suggestionsLoading={suggestionsLoading}
            isAutoComplete={false}
            searchParameters={searchFilters}
            setSearchFilters={setSearchFilters}
            classificationsFilter={classificationsFilter}
            isFromProject={true}
            financingNeed={financingNeeds.byId[projectId]}
            dropdownFilterVisible={dropdownFilterVisible}
            setDropdownFilterVisible={setDropdownFilterVisible}
            isNgo={true}
            onSelectAlgorithm={onSelectAlgorithm}
          />
        </div>
        <DonorCards
          redirectNewPage
          donors={
            searchResult?.resultsIds?.reduce(
              (list: Donor[], donorId) =>
                donors.byId[donorId] ? [...list, donors.byId[donorId]] : list,
              [],
            ) || null
          }
          onTableChange={onTableChange}
          loading={loading}
          pagination={{
            total: min([
              searchResult?.resultsCount,
              DONORS_MAX_RESULTS * MAX_DISPLAYED_PAGES_NUMBER,
            ]),
            current: offset / DONORS_MAX_RESULTS + 1,
            pageSize: DONORS_MAX_RESULTS,
            showSizeChanger: false,
          }}
          financingNeedId={displayedFinancingNeed._id}
        />
      </div>
    );
  } else if (displayedFinancingNeed && !isPremium) {
    history.push('/projects');
    return (
      <div>
        <p>You do not have a valid subscription to access this feature.</p>
      </div>
    );
  } else {
    return <FullscreenSpin />;
  }
};
